import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  IconButton,
  Paper,
  Avatar,
  Chip,
  Alert,
  CircularProgress,
  Stack,
  Button,
  Tooltip,
} from '@mui/material';
import {
  Send as SendIcon,
  SmartToy as BotIcon,
  Person as PersonIcon,
  Error as ErrorIcon,
  Refresh as RefreshIcon,
  Timeline as TimelineIcon,
  Visibility as VisibilityIcon,
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { agentsService } from '../services/agentsService'; // 🆕 Import para streaming

// 🆕 Estilos para animações de streaming
const streamingStyles = {
  '@keyframes blink': {
    '0%': { opacity: 1 },
    '50%': { opacity: 0 },
    '100%': { opacity: 1 },
  },
  '@keyframes pulse': {
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.05)' },
    '100%': { transform: 'scale(1)' },
  },
};

const AgentTester = ({ 
  agentId, 
  messages, 
  onSendMessage,
  onNewConversation = null,
  onTraceData = null,
  disabled = false,
  streamingEnabled = false,  // 🆕 Prop para controlar streaming
  messageDelay = 0  // 🆕 Prop para detectar delay configurado
}) => {
  const [inputMessage, setInputMessage] = useState('');
  const [loading, setLoading] = useState(false);
  const [threadId, setThreadId] = useState(null);
  const [conversationCount, setConversationCount] = useState(0);
  const [traceMode, setTraceMode] = useState(true); // Trace ativo por padrão independente do streaming
  const [lastTraceData, setLastTraceData] = useState(null); // Armazenar último trace
  const [streamingText, setStreamingText] = useState(''); // 🆕 Texto sendo streamado
  const [isStreaming, setIsStreaming] = useState(false); // 🆕 Estado de streaming ativo
  const messagesEndRef = useRef(null);
  
  // WebSocket removido - usando API Externa com buffer HTTP

  // Scroll automático para última mensagem e durante streaming
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, streamingText]); // 🆕 Incluir streamingText para scroll durante streaming

  // WebSocket removido - API Externa com buffer HTTP substituiu essa funcionalidade

  // WebSocket removido - API Externa com buffer HTTP substituiu essa funcionalidade

  const handleSendMessage = async () => {
    if (!inputMessage.trim() || disabled) return;
    
    const message = inputMessage.trim();
    setInputMessage('');

    // 🚀 AGORA TODOS OS TESTES USAM API EXTERNA (que já tem buffer HTTP)
    // WebSocket removido - API Externa tem buffer integrado

    // 🔄 FALLBACK PARA HTTP TRADICIONAL
    setLoading(true);

    try {
      // ✅ SEMPRE ADICIONAR MENSAGEM DO USUÁRIO PRIMEIRO (para todos os modos)
      if (onSendMessage) {
        await onSendMessage(message, threadId, false, { type: 'user_only' });
      }

      // 🆕 DECIDIR ENTRE STREAMING E MODO TRADICIONAL
      if (streamingEnabled) {
        // ✨ MODO STREAMING REAL
        console.log(`🚀 [AGENT_TESTER] Iniciando streaming... (trace: ${traceMode ? 'ON' : 'OFF'})`);
        setIsStreaming(true);
        setStreamingText('');
        
        await agentsService.testAgentStream(
          agentId, 
          message, 
          threadId,
          traceMode, // 🆕 Passar se quer trace ou não
          (streamEvent) => {
            if (streamEvent.type === 'token') {
              // 📡 Atualizar texto progressivamente
              setStreamingText(streamEvent.accumulatedText);
            } else if (streamEvent.type === 'complete') {
              // ✅ Streaming finalizado
              console.log('✅ [AGENT_TESTER] Streaming completo');
              setIsStreaming(false);
              
              // Atualizar thread_id
              if (streamEvent.thread_id) {
                setThreadId(streamEvent.thread_id);
                
                // Se é a primeira mensagem, incrementar contador
                if (!threadId) {
                  setConversationCount(prev => prev + 1);
                }
              }

              // Chamar onSendMessage para adicionar mensagens finais ao estado
              if (onSendMessage) {
                const mockResponse = {
                  answer: streamEvent.finalAnswer,
                  thread_id: streamEvent.thread_id,
                  streaming: true,
                  trace: (traceMode && streamEvent.trace) ? streamEvent.trace : null,  // 🆕 Incluir trace apenas se traceMode ativo
                  billing: streamEvent.billing || null  // 🆕 Incluir billing se disponível
                };
                
                // Simular chamada tradicional para manter compatibilidade
                onSendMessage(message, threadId, false, mockResponse);
              }
              
              // 🆕 Atualizar trace se disponível E se traceMode está ativo
              if (traceMode && streamEvent.trace && onTraceData) {
                console.log('🔍 [AGENT_TESTER] Atualizando trace data do streaming:', streamEvent.trace);
                setLastTraceData(streamEvent.trace);
                onTraceData(streamEvent.trace);
              }
              
              // Limpar texto temporário
              setStreamingText('');
            } else if (streamEvent.type === 'stream_error') {
              // 🔧 Erro de streaming - resetar estado
              console.error('💥 [AGENT_TESTER] Erro de streaming:', streamEvent.error);
              setIsStreaming(false);
              setStreamingText('');
              
              // Adicionar mensagem de erro ao chat
              if (onSendMessage) {
                const errorResponse = {
                  answer: `Erro no streaming: ${streamEvent.error}`,
                  thread_id: threadId,
                  streaming: false
                };
                onSendMessage(message, threadId, false, errorResponse);
              }
            }
          }
        );

      } else {
        // 🔄 MODO TRADICIONAL (agente sem streaming)
        console.log(`🔄 [AGENT_TESTER] Usando modo tradicional... (trace: ${traceMode ? 'ON' : 'OFF'})`);
        
        // 🔄 CHAMAR API (mensagem do usuário já foi adicionada acima)
        const response = await onSendMessage(message, threadId, traceMode);
        
        // Atualizar thread_id com o retorno da API
        if (response?.thread_id) {
          setThreadId(response.thread_id);
          
          // Se é a primeira mensagem, incrementar contador
          if (!threadId) {
            setConversationCount(prev => prev + 1);
          }
        }

        // Se trace mode está ativo e há dados de trace, repassar para componente pai
        if (traceMode && response?.trace && onTraceData) {
          const traceDataWithTimestamp = {
            ...response,
            timestamp: new Date().toISOString()
          };
          setLastTraceData(traceDataWithTimestamp); // Salvar último trace
          onTraceData(traceDataWithTimestamp);
        }
      }
    } catch (error) {
      console.error('Erro ao enviar mensagem:', error);
      setIsStreaming(false);
      setStreamingText('');
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    setThreadId(null);
    setConversationCount(prev => prev + 1);
    
    // Notificar componente pai para limpar mensagens
    if (onNewConversation) {
      onNewConversation();
    }
  };

  const handleShowLastTrace = () => {
    if (lastTraceData && onTraceData) {
      onTraceData(lastTraceData);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const clearChat = () => {
    handleNewConversation();
  };

  const getMessageIcon = (type) => {
    switch (type) {
      case 'user':
        return <PersonIcon />;
      case 'agent':
        return <BotIcon />;
      case 'error':
        return <ErrorIcon />;
      default:
        return <BotIcon />;
    }
  };

  const getMessageColor = (type) => {
    switch (type) {
      case 'user':
        return 'primary.main';
      case 'agent':
        return 'success.main';
      case 'error':
        return 'error.main';
      default:
        return 'grey.500';
    }
  };

  const formatTime = (timestamp) => {
    return new Date(timestamp).toLocaleTimeString('pt-BR', {
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const suggestedQuestions = [
    "Olá! Como você pode me ajudar?",
    "Quais são suas principais funcionalidades?",
    "Pode me ajudar com informações sobre produtos?",
    "Como posso agendar uma reunião?",
  ];

  return (
    <Box 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        minHeight: 0, // Importante para flex
      }}
    >
      {/* Área de Mensagens */}
      <Box 
        sx={{ 
          flex: 1, 
          overflow: 'auto', 
          p: 2,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          ...streamingStyles, // 🆕 Aplicar estilos de animação
        }}
      >
        {/* Estado inicial */}
        {messages.length === 0 && (
          <Box sx={{ textAlign: 'center', py: 4 }}>
            <Avatar 
              sx={{ 
                width: 64, 
                height: 64, 
                mx: 'auto', 
                mb: 2,
                bgcolor: 'primary.main',
              }}
            >
              <BotIcon sx={{ fontSize: 32 }} />
            </Avatar>
            <Typography variant="h6" sx={{ mb: 1, fontWeight: 600 }}>
              Teste seu Agente
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Envie uma mensagem para testar o comportamento do agente
            </Typography>

            {/* Perguntas sugeridas */}
            {!disabled && (
              <Stack spacing={1}>
                <Typography variant="caption" color="text.secondary">
                  Perguntas sugeridas:
                </Typography>
                {suggestedQuestions.map((question, index) => (
                  <Chip
                    key={index}
                    label={question}
                    variant="outlined"
                    size="small"
                    onClick={() => setInputMessage(question)}
                    sx={{ 
                      cursor: 'pointer',
                      '&:hover': { 
                        backgroundColor: 'action.hover' 
                      },
                    }}
                  />
                ))}
              </Stack>
            )}

            {disabled && (
              <Alert severity="info" sx={{ mt: 2 }}>
                Salve o agente para habilitar o teste
              </Alert>
            )}
          </Box>
        )}

        {/* Lista de Mensagens */}
        <AnimatePresence>
          {messages.map((message, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.3 }}
            >
              <Box
                sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'flex-start',
                  flexDirection: message.type === 'user' ? 'row-reverse' : 'row',
                }}
              >
                <Avatar 
                  sx={{ 
                    bgcolor: getMessageColor(message.type),
                    width: 36,
                    height: 36,
                  }}
                >
                  {getMessageIcon(message.type)}
                </Avatar>

                <Box 
                  sx={{ 
                    flex: 1,
                    maxWidth: '80%',
                  }}
                >
                  <Paper
                    variant="outlined"
                    sx={{
                      p: 2,
                      bgcolor: message.type === 'user' ? 'primary.light' : 
                               message.type === 'error' ? 'error.light' : 'background.paper',
                      color: message.type === 'user' ? 'primary.contrastText' : 'text.primary',
                      borderRadius: 2,
                      wordBreak: 'break-word',
                    }}
                  >
                    <Typography 
                      variant="body2" 
                      sx={{ 
                        whiteSpace: 'pre-wrap',
                        lineHeight: 1.5,
                      }}
                    >
                      {message.content}
                    </Typography>
                  </Paper>

                  <Typography 
                    variant="caption" 
                    color="text.secondary"
                    sx={{ 
                      display: 'block', 
                      mt: 0.5,
                      textAlign: message.type === 'user' ? 'right' : 'left',
                    }}
                  >
                    {formatTime(message.timestamp)}
                  </Typography>
                </Box>
              </Box>
            </motion.div>
          ))}
        </AnimatePresence>

        {/* 🆕 Mensagem sendo streamada em tempo real */}
        {isStreaming && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
                flexDirection: 'row', // Sempre lado esquerdo para bot
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: 'secondary.main',
                  width: 36,
                  height: 36,
                  animation: 'pulse 1.5s infinite', // Animação pulsante
                }}
              >
                <BotIcon />
              </Avatar>

              <Box 
                sx={{ 
                  flex: 1,
                  maxWidth: '80%',
                }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    bgcolor: 'background.paper',
                    color: 'text.primary',
                    borderRadius: 2,
                    wordBreak: 'break-word',
                    border: '2px solid',
                    borderColor: 'primary.main', // Destaque visual
                    position: 'relative',
                  }}
                >
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      whiteSpace: 'pre-wrap',
                      lineHeight: 1.5,
                      fontStyle: streamingText ? 'normal' : 'italic', // 🤔 Itálico quando está pensando
                      color: streamingText ? 'text.primary' : 'text.secondary', // 🤔 Cor mais suave quando está pensando
                    }}
                  >
                    {streamingText || 'estou pensando...'}
                    {/* 🤔 Cursor só aparece quando há texto real (não quando está pensando) */}
                    {streamingText && (
                      <Box 
                        component="span" 
                        sx={{ 
                          display: 'inline-block',
                          width: '8px',
                          height: '16px',
                          bgcolor: 'primary.main',
                          animation: 'blink 1s infinite',
                          marginLeft: '2px'
                        }}
                      />
                    )}
                  </Typography>
                  
                  {/* Badge indicando streaming ativo */}
                  <Chip
                    label="Streaming..."
                    size="small"
                    color="primary"
                    sx={{
                      position: 'absolute',
                      top: -8,
                      right: -8,
                      fontSize: '0.7rem',
                      height: 20,
                    }}
                  />
                </Paper>

                <Typography 
                  variant="caption" 
                  color="text.secondary"
                  sx={{ 
                    mt: 0.5,
                    textAlign: 'left',
                  }}
                >
                  Agora • Streaming ativo
                </Typography>
              </Box>
            </Box>
          </motion.div>
        )}

        {/* Indicador de carregamento */}
        {loading && !isStreaming && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
          >
            <Box
              sx={{
                display: 'flex',
                gap: 2,
                alignItems: 'flex-start',
              }}
            >
              <Avatar 
                sx={{ 
                  bgcolor: 'success.main',
                  width: 36,
                  height: 36,
                }}
              >
                <BotIcon />
              </Avatar>

              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  borderRadius: 2,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                }}
              >
                <CircularProgress size={16} />
                <Typography variant="body2" color="text.secondary">
                  Agente está digitando...
                </Typography>
              </Paper>
            </Box>
          </motion.div>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Input de Mensagem */}
      <Box 
        sx={{ 
          p: 2, 
          borderTop: '1px solid', 
          borderColor: 'divider',
          backgroundColor: 'background.paper',
        }}
      >
        {/* Informações da Thread */}
        {threadId && (
          <Box sx={{ 
            display: 'flex', 
            justifyContent: 'space-between', 
            alignItems: 'center',
            mb: 1,
            p: 1,
            bgcolor: 'background.default',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}>
            <Typography variant="caption" color="text.secondary">
              🔗 Thread: {threadId.substring(0, 8)}...
            </Typography>
            <Button
              size="small"
              onClick={handleNewConversation}
              sx={{ fontSize: '0.7rem', minWidth: 'auto', p: 0.5 }}
            >
              Nova Conversa
            </Button>
          </Box>
        )}
        
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-end' }}>
          <TextField
            fullWidth
            multiline
            maxRows={4}
            placeholder={
              disabled ? 'Salve o agente para enviar mensagens' : 
              'Digite sua mensagem...'
            }
            value={inputMessage}
            onChange={(e) => setInputMessage(e.target.value)}
            onKeyPress={handleKeyPress}
            disabled={disabled}
            // 🆕 PERMITIR INPUT DURANTE LOADING/STREAMING PARA TESTE DE BUFFER
            variant="outlined"
            size="small"
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 2,
              },
            }}
          />
          
          {/* Botão de Trace - Discreto */}
          <Tooltip title={traceMode ? "Desativar trace nas próximas consultas" : "Ativar trace nas próximas consultas"} placement="top">
            <IconButton
              onClick={() => setTraceMode(!traceMode)}
              disabled={disabled}
              size="small"
              sx={{
                bgcolor: traceMode ? 'secondary.main' : 'transparent',
                color: traceMode ? 'white' : 'text.secondary',
                border: 1,
                borderColor: traceMode ? 'secondary.main' : 'divider',
                '&:hover': {
                  bgcolor: traceMode ? 'secondary.dark' : 'action.hover',
                  borderColor: 'secondary.main',
                },
                '&.Mui-disabled': {
                  bgcolor: 'transparent',
                  color: 'action.disabled',
                  borderColor: 'action.disabled',
                },
              }}
            >
              <TimelineIcon fontSize="small" />
            </IconButton>
          </Tooltip>

          {/* Botão Ver Último Trace */}
          {lastTraceData && (
            <Tooltip title="Ver trace da última consulta" placement="top">
              <IconButton
                onClick={handleShowLastTrace}
                disabled={disabled}
                size="small"
                sx={{
                  bgcolor: 'info.main',
                  color: 'white',
                  border: 1,
                  borderColor: 'info.main',
                  '&:hover': {
                    bgcolor: 'info.dark',
                    borderColor: 'info.dark',
                  },
                  '&.Mui-disabled': {
                    bgcolor: 'transparent',
                    color: 'action.disabled',
                    borderColor: 'action.disabled',
                  },
                }}
              >
                <VisibilityIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          
          <IconButton
            color="primary"
            onClick={handleSendMessage}
            disabled={!inputMessage.trim() || disabled}
            // 🆕 PERMITIR ENVIO DURANTE LOADING PARA TESTE DE BUFFER
            sx={{
              bgcolor: 'primary.main',
              color: 'white',
              '&:hover': {
                bgcolor: 'primary.dark',
              },
              '&.Mui-disabled': {
                bgcolor: 'action.disabledBackground',
                color: 'action.disabled',
              },
            }}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              <SendIcon />
            )}
          </IconButton>
        </Box>

        {/* Dicas */}
        <Typography 
          variant="caption" 
          color="text.secondary" 
          sx={{ display: 'block', mt: 1, textAlign: 'center' }}
        >
          {disabled ? 
            'Configure e salve o agente para começar a testar' :
            isStreaming ?
              '📡 Streaming ativo - Resposta sendo gerada em tempo real...' :
            loading ?
              '⏱️ Processando mensagem - Você pode enviar mais mensagens para testar o buffer...' :
            streamingEnabled && messageDelay > 0 && traceMode ?
              `✨🔍 Streaming + Buffer + Trace (${messageDelay}s) - Modo híbrido completo com análise detalhada${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            streamingEnabled && messageDelay > 0 && !traceMode ?
              `✨ Streaming + Buffer (${messageDelay}s) - Modo híbrido! Mensagens combinadas e streaming em tempo real${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            streamingEnabled && messageDelay === 0 && traceMode ?
              `⚡🔍 Streaming + trace ativo - Resposta progressiva com análise detalhada${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            streamingEnabled && messageDelay === 0 && !traceMode ?
              `⚡ Streaming ativo - Resposta progressiva em tempo real${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            !streamingEnabled && messageDelay > 0 && traceMode ?
              `🔄🔍 Buffer + Trace (${messageDelay}s) - Combina mensagens com análise detalhada${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            !streamingEnabled && messageDelay > 0 && !traceMode ?
              `🔄 Buffer HTTP ativo (${messageDelay}s) - Teste enviando mensagens rapidamente via API Externa! • Pressione Enter para enviar${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            !streamingEnabled && traceMode ?
              `🔍 Modo trace ativo - Análise detalhada da execução será capturada${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            threadId ? 
              `Contexto de conversa mantido • Pressione Enter para enviar${lastTraceData ? ' • 👁️ Ver último trace' : ''}` :
            `Pressione Enter para enviar, Shift + Enter para nova linha${lastTraceData ? ' • 👁️ Ver último trace disponível' : ''}`
          }
        </Typography>
      </Box>
    </Box>
  );
};

export default AgentTester; 